import React, { useState, useEffect, useMemo } from 'react';
import Modal from '../shared/Modal';
import { getAuditLogs } from '../../services/logging/auditLogger';
import { format, isToday, isYesterday } from 'date-fns';
import { tr } from 'date-fns/locale';
import { STATUS_LABELS } from '../../constants/statuses';

const FIELD_LABELS = {
    'plateNumber': 'Plaka No',
    'driverName': 'Sürücü Adı',
    'driverPhone': 'Sürücü Telefon',
    'vehicleType': 'Araç Tipi',
    'vehicleTypeId': 'Araç Tipi ID',
    'vehicleTypeName': 'Araç Tipi',
    'transporterName': 'Nakliyeci Firma',
    'transporterShortName': 'Nakliyeci (Kısa)',
    'dockName': 'Rampa',
    'dockId': 'Rampa ID',
    'startTime': 'Başlangıç Saati',
    'endTime': 'Bitiş Saati',
    'notes': 'Notlar',
    'documentNumber': 'Belge No',
    'status': 'Durum',
    'bookingDate': 'Rezervasyon Tarihi',
    'reservationNumber': 'Rezervasyon No',
    'referenceNumber': 'Referans No'
};

const ACTION_LABELS = {
    'CREATE': 'OLUŞTURMA',
    'UPDATE_FIELD': 'DÜZENLEME',
    'STATUS_CHANGE': 'DURUM GÜNCELLEME',
    'NOTE_ADDED': 'NOT EKLEME',
    'TIME_CHANGED': 'SAAT DEĞİŞİMİ',
    'RESERVATION_CREATE': 'YENİ KAYIT',
    'RESERVATION_UPDATE': 'GÜNCELLEME',
    'RESERVATION_DELETE': 'SİLME',
    'RESERVATION_SCHEDULE': 'PLANLAMA',
    'RESERVATION_MOVE': 'TAŞIMA',
    'AUTO_ASSIGN': 'OTOMATİK ATAMA'
};

const HistoryLogModal = ({ isOpen, onClose, entityId, tenantId, title = "Geçmiş Hareketler", baseData = null }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isOpen && entityId) {
            loadLogs(entityId);
        } else {
            setLogs([]);
        }
    }, [isOpen, entityId]);

    const loadLogs = async (id) => {
        setLoading(true);
        const data = await getAuditLogs(id, tenantId);
        // data from getAuditLogs already has timestamp as Date objects
        setLogs(data);
        setLoading(false);
    };

    const formatValue = (key, value) => {
        if (value === null || value === undefined || value === '') return 'Belirtilmedi';
        if (key === 'status') return STATUS_LABELS[value] || value;
        // Handle nested vehicleType objects
        if (key === 'vehicleType' && typeof value === 'object' && value !== null) {
            return value.name || value.id || 'Belirtilmedi';
        }
        return String(value);
    };

    const formatKey = (key) => FIELD_LABELS[key] || key;

    const groupedLogs = useMemo(() => {
        let allLogs = [...logs];

        // Synthesize creation log if missing and baseData is available
        const hasCreationLog = allLogs.some(l => l.actionType === 'CREATE' || l.actionType === 'RESERVATION_CREATE' || l.actionType === 'CREATION');
        if (!hasCreationLog && baseData && (baseData.createdBy || baseData.createdAt || baseData.uid)) {
            allLogs.push({
                id: 'synthetic-create',
                actionType: 'CREATE',
                timestamp: baseData.createdAt?.toDate ? baseData.createdAt.toDate() : (baseData.createdAt ? new Date(baseData.createdAt) : new Date(baseData.updatedAt || Date.now())),
                performedBy: {
                    uid: baseData.createdBy || 'system',
                    name: baseData.creatorName || baseData.createdBy || 'Sistem',
                    role: baseData.creatorRole || ''
                },
                changes: baseData
            });
        }

        if (!allLogs.length) return {};
        const groups = {};
        allLogs.sort((a, b) => b.timestamp - a.timestamp).forEach(log => {
            const date = log.timestamp;
            let groupTitle = format(date, 'd MMMM yyyy', { locale: tr });
            if (isToday(date)) groupTitle = 'Bugün';
            else if (isYesterday(date)) groupTitle = 'Dün';

            if (!groups[groupTitle]) groups[groupTitle] = [];
            groups[groupTitle].push(log);
        });
        return groups;
    }, [logs, baseData]);

    const renderChanges = (log) => {
        const { actionType, changes } = log;

        const chipStyle = {
            padding: '4px 14px',
            borderRadius: '999px',
            fontSize: '11px',
            fontWeight: '900',
            backdropFilter: 'blur(8px)',
            border: '1px solid',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s'
        };

        const oldChip = {
            ...chipStyle,
            backgroundColor: 'rgba(239, 68, 68, 0.15)',
            color: '#ef4444',
            borderColor: 'rgba(239, 68, 68, 0.3)',
            textDecoration: 'line-through'
        };

        const newChip = {
            ...chipStyle,
            backgroundColor: 'rgba(34, 197, 94, 0.15)',
            color: '#16a34a',
            borderColor: 'rgba(34, 197, 94, 0.3)',
            boxShadow: '0 0 15px rgba(34, 197, 94, 0.1)',
            fontWeight: '950'
        };

        if (actionType === 'STATUS_CHANGE' || (actionType === 'UPDATE_FIELD' && (changes?.status || changes?.newStatus))) {
            const status = changes.status || { old: changes.oldStatus, new: changes.newStatus };
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', borderRadius: '12px', backgroundColor: 'var(--bg-hover)', border: '1px solid var(--border-color)' }}>
                    <span style={{ fontSize: '11px', fontWeight: '900', color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.1em', minWidth: '60px' }}>DURUM:</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={oldChip}>{formatValue('status', status.old)}</span>
                        <span style={{ color: '#4b5563', fontWeight: '900' }}>→</span>
                        <span style={{ ...newChip, ring: '1px solid rgba(34, 197, 94, 0.3)' }}>{formatValue('status', status.new)}</span>
                    </div>
                </div>
            );
        }

        if (actionType === 'TIME_CHANGED') {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px', borderRadius: '8px', backgroundColor: 'rgba(236, 72, 153, 0.05)', border: '1px solid rgba(236, 72, 153, 0.15)' }}>
                    <span style={{ fontSize: '10px', fontWeight: '900', color: '#f472b6', textTransform: 'uppercase' }}>SAAT:</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                        <span style={{ color: '#9ca3af', textDecoration: 'line-through' }}>{changes.old || 'N/A'}</span>
                        <span style={{ color: '#6b7280' }}>→</span>
                        <span style={{ color: '#f06292', fontWeight: '900' }}>{changes.new || 'N/A'}</span>
                    </div>
                </div>
            );
        }

        if (changes?.description) {
            return (
                <div style={{ padding: '10px', borderRadius: '8px', backgroundColor: 'var(--bg-hover)', border: '1px solid var(--border-color)', fontSize: '13px', color: 'var(--text-primary)' }}>
                    {changes.description}
                </div>
            );
        }

        if (actionType === 'NOTE_ADDED') {
            return (
                <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: 'rgba(245, 158, 11, 0.1)', borderLeft: '4px solid #f59e0b' }}>
                    <p style={{ fontSize: '13px', color: 'var(--text-primary)', opacity: 0.9, fontStyle: 'italic', margin: 0 }}>"{changes.note}"</p>
                </div>
            );
        }

        if (actionType === 'UPDATE_FIELD' || actionType === 'RESERVATION_UPDATE') {
            const keys = Object.keys(changes || {}).filter(k =>
                !['lastActionBy', 'lastActionRole', 'updatedAt', 'status', 'id', 'tenantId'].includes(k) &&
                changes[k].old !== changes[k].new
            );

            const filteredKeys = keys.filter(k => {
                if (k === 'vehicleType' && keys.includes('vehicleTypeName')) return false;
                if (k === 'vehicleTypeId' && keys.includes('vehicleTypeName')) return false;
                if (k === 'dockId' && keys.includes('dockName')) return false;
                if (k === 'transporterId' && keys.includes('transporterName')) return false;
                return true;
            });

            if (filteredKeys.length === 0) return <span style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>Sistem güncellemesi</span>;

            return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' }}>
                    {filteredKeys.map(key => (
                        <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', borderRadius: '16px', backgroundColor: 'var(--bg-hover)', border: '1px solid var(--border-color)', transition: 'all 0.3s' }}>
                            <span style={{ fontSize: '11px', fontWeight: '950', color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '0.15em', borderBottom: '1px solid rgba(96, 165, 250, 0.15)', paddingBottom: '6px', marginBottom: '4px' }}>
                                {formatKey(key)}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <span style={oldChip}>{formatValue(key, changes[key].old)}</span>
                                <span style={{ color: '#4b5563', fontWeight: '900', fontSize: '14px' }}>→</span>
                                <span style={newChip}>{formatValue(key, changes[key].new)}</span>
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        if (actionType === 'CREATE' || actionType === 'RESERVATION_CREATE') {
            return (
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', color: '#4ade80', fontWeight: '500', fontSize: '14px' }}>
                    <span style={{ fontSize: '18px' }}>✨</span>
                    <span>Rezervasyon sisteme kaydedildi.</span>
                    {(changes?.reservationNumber || changes?.referenceNumber || changes?.docNo) && (
                        <span style={{ color: 'rgba(74, 222, 128, 0.7)', fontSize: '12px', fontWeight: 'bold', marginLeft: 'auto' }}>
                            [ {changes?.reservationNumber || changes?.referenceNumber || changes?.docNo} ]
                        </span>
                    )}
                </div>
            );
        }

        return <span style={{ fontSize: '12px', color: '#6b7280' }}>-</span>;
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            size="xl"
        >
            <div className="min-h-[400px] max-h-[75vh] overflow-y-auto px-lg py-md custom-scrollbar" style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: '16px', color: '#94a3b8' }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', border: '2px solid rgba(59, 130, 246, 0.2)', borderTopColor: '#3b82f6', animation: 'spin 1s linear infinite' }}></div>
                        <p>Geçmiş yükleniyor...</p>
                        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                    </div>
                ) : Object.keys(groupedLogs).length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', color: '#94a3b8', opacity: 0.5 }}>
                        <span style={{ fontSize: '48px', marginBottom: '16px' }}>🕒</span>
                        <p>Bu kayıt için henüz bir geçmiş bulunmuyor.</p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        {Object.entries(groupedLogs).map(([date, items]) => (
                            <div key={date} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <h3 style={{ fontSize: '13px', fontWeight: '900', color: '#3b82f6', whiteSpace: 'nowrap', padding: '4px 16px', borderRadius: '99px', backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)', margin: 0 }}>
                                        {date}
                                    </h3>
                                    <div style={{ height: '1px', width: '100%', background: 'linear-gradient(to right, rgba(59, 130, 246, 0.2), transparent)' }}></div>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingLeft: '16px', position: 'relative' }}>
                                    <div style={{ position: 'absolute', left: '6px', top: 0, bottom: 0, width: '2px', backgroundColor: 'var(--border-color)', opacity: 0.3 }}></div>
                                    {items.map((log, idx) => (
                                        <div key={log.id || idx} style={{ position: 'relative' }}>
                                            <div style={{ position: 'absolute', left: '-14px', top: '16px', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#4b5563', border: '2px solid var(--bg-secondary)', zIndex: 10 }}></div>

                                            <div className="card-premium glow-theme p-md" style={{ backgroundColor: 'var(--bg-card)', display: 'flex', flexDirection: 'column', gap: '14px', marginLeft: '16px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <span style={{ fontSize: '16px', fontWeight: '900', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                                                            {format(log.timestamp, 'HH:mm')}
                                                        </span>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <span style={{ fontSize: '14px', fontWeight: '950', color: 'var(--text-primary)', opacity: 0.9 }}>
                                                                {log.performedBy?.fullName || log.performedBy?.name || 'Sistem'}
                                                            </span>
                                                            <span style={{ color: '#4b5563', fontWeight: '900' }}>•</span>
                                                            <span style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: '900', backgroundColor: 'var(--bg-hover)', border: '1px solid var(--border-color)', padding: '2px 8px', borderRadius: '4px' }}>
                                                                {log.performedBy?.role === 'coordinator' ? 'Koordinatör' :
                                                                    log.performedBy?.role === 'transporter' ? 'Nakliyeci' :
                                                                        log.performedBy?.role || ''}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <span style={{
                                                        padding: '4px 10px', borderRadius: '4px', fontSize: '9px', fontWeight: '950', letterSpacing: '0.05em', border: '1px solid',
                                                        backgroundColor: log.actionType === 'CREATE' ? 'rgba(34, 197, 94, 0.1)' :
                                                            log.actionType === 'STATUS_CHANGE' ? 'rgba(168, 85, 247, 0.1)' :
                                                                log.actionType === 'TIME_CHANGED' ? 'rgba(236, 72, 153, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                                                        color: log.actionType === 'CREATE' ? '#4ade80' :
                                                            log.actionType === 'STATUS_CHANGE' ? '#a855f7' :
                                                                log.actionType === 'TIME_CHANGED' ? '#f472b6' : '#60a5fa',
                                                        borderColor: log.actionType === 'CREATE' ? 'rgba(34, 197, 94, 0.2)' :
                                                            log.actionType === 'STATUS_CHANGE' ? 'rgba(168, 85, 247, 0.2)' :
                                                                log.actionType === 'TIME_CHANGED' ? 'rgba(236, 72, 153, 0.2)' : 'rgba(59, 130, 246, 0.2)'
                                                    }}>
                                                        {ACTION_LABELS[log.actionType] || log.actionType}
                                                    </span>
                                                </div>

                                                <div style={{ marginTop: '4px' }}>
                                                    {renderChanges(log)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {!loading && logs.length > 0 && (
                <div style={{ padding: '8px 24px', textAlign: 'right', backgroundColor: 'rgba(255, 255, 255, 0.02)', borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    <span style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', fontWeight: '900' }}>
                        TOPLAM {logs.length} HAREKET KAYDI
                    </span>
                </div>
            )}
        </Modal>
    );
};

export default HistoryLogModal;
